<template>
  <el-dialog
    :title="dialogTitle"
    :visible.sync="innerVisible"
    width="720px"
    :close-on-click-modal="false"
    append-to-body
    @open="handleOpen">

    <!-- 步骤条 (stepper 模式下显示) -->
    <el-steps v-if="stepper" :active="currentStep" finish-status="success" simple style="margin-bottom:16px">
      <el-step title="确认会议信息"></el-step>
      <el-step title="结算细节"></el-step>
      <el-step title="确认提交"></el-step>
    </el-steps>

    <el-form :model="form" :rules="rules" label-width="110px" ref="formRef" size="small">
      <!-- ============ Step 1: 确认会议信息 ============ -->
      <div v-show="!stepper || currentStep === 0">
      <el-form-item label="结算类型" prop="settlementType">
        <el-radio-group v-model="form.settlementType">
          <el-radio-button label="SUPPLIER">供应商结算</el-radio-button>
          <el-radio-button label="LABOR">劳务结算</el-radio-button>
        </el-radio-group>
      </el-form-item>

      <el-form-item label="结算单号" prop="orderNo">
        <el-input v-model="form.orderNo" placeholder="例如 SUPP-20260518-001" />
        <div class="form-hint">建议格式: 业务前缀-日期-序号</div>
      </el-form-item>

      <!-- 从会议跳来时, 这三项已由会议确定, 显示为只读静态文本 -->
      <template v-if="isLockedFromMeeting">
        <el-form-item label="所属机构">
          <div class="readonly-field">
            <i class="el-icon-office-building"></i>
            <span>{{ lockedOrgName }}</span>
            <el-tag size="mini" type="info" effect="plain">来自会议</el-tag>
          </div>
        </el-form-item>
        <el-form-item label="关联项目">
          <div class="readonly-field">
            <i class="el-icon-collection"></i>
            <span>{{ lockedProjectName }}</span>
            <el-tag size="mini" type="info" effect="plain">来自会议</el-tag>
          </div>
        </el-form-item>
        <el-form-item label="关联会议">
          <div class="readonly-field">
            <i class="el-icon-video-camera"></i>
            <span>{{ lockedMeetingName }}</span>
            <el-tag size="mini" type="info" effect="plain">来自会议</el-tag>
          </div>
          <div class="form-hint">如需关联多场会议批量结算,请到"结算单管理"标准流程新建</div>
        </el-form-item>
      </template>

      <!-- 标准新建流程(从结算单管理进入), 用户从全量列表挑选 -->
      <template v-else>
        <el-form-item label="所属机构" prop="orgId">
          <virtual-select
            v-model="form.orgId"
            :options="orgOptions"
            label-key="orgName" value-key="id" item-key="id"
            placeholder="请选择机构"
            clearable
            style="width:100%"
            @change="onOrgChange" />
        </el-form-item>

        <el-form-item label="关联项目" prop="projectId">
          <el-select
            v-model="form.projectId"
            :disabled="!form.orgId"
            filterable
            :loading="projectLoading"
            :placeholder="form.orgId ? '请选择项目' : '请先选择机构'"
            clearable
            style="width:100%"
            @change="onProjectChange">
            <el-option v-for="p in projectOptions" :key="p.id" :label="p.projectName" :value="p.id" />
          </el-select>
        </el-form-item>

        <el-form-item label="关联会议">
          <el-select
            v-model="selectedMeetingIds"
            :disabled="!form.projectId"
            multiple
            filterable
            :loading="meetingLoading"
            :placeholder="form.projectId ? '可多选会议' : '请先选择项目'"
            clearable
            style="width:100%">
            <el-option v-for="m in meetingOptions" :key="m.id" :label="m.meetingName" :value="m.id" />
          </el-select>
          <div class="form-hint">劳务结算建议每场会单独结;供应商结算可关联多场</div>
        </el-form-item>
      </template>
      </div>

      <!-- ============ Step 2: 结算细节 ============ -->
      <div v-show="!stepper || currentStep === 1">
      <el-form-item label="结算模式" prop="settlementMode">
        <el-select v-model="form.settlementMode" style="width:100%">
          <el-option label="按项目结算 (PROJECT)" value="PROJECT" />
          <el-option label="按 N 场会结算 (BATCH)" value="BATCH" />
          <el-option label="每场会单结 (PER_MEETING)" value="PER_MEETING" />
        </el-select>
      </el-form-item>

      <el-form-item v-if="form.settlementMode === 'BATCH'" label="批量阈值">
        <el-input-number v-model="form.settlementThreshold" :min="1" :max="999" style="width:100%" />
        <div class="form-hint">达到该会议数量后可结算</div>
      </el-form-item>

      <template v-if="form.settlementType === 'SUPPLIER'">
        <el-form-item label="供应商">
          <virtual-select
            v-model="form.supplierOrgId"
            :options="supplierOptions"
            label-key="orgName" value-key="id" item-key="id"
            placeholder="请选择供应商"
            clearable
            style="width:100%"
            @change="onSupplierChange" />
        </el-form-item>

        <el-form-item label="采购方">
          <virtual-select
            v-model="form.buyerOrgId"
            :options="orgOptions"
            label-key="orgName" value-key="id" item-key="id"
            placeholder="请选择采购方"
            clearable
            style="width:100%"
            @change="onBuyerChange" />
        </el-form-item>
      </template>

      <el-form-item label="收款方式">
        <el-select v-model="form.collectWay" clearable style="width:100%">
          <el-option v-for="o in collectWayOptions" :key="o.id || o.dictLabel" :label="o.dictLabel" :value="o.dictLabel" />
        </el-select>
      </el-form-item>

      <el-form-item label="备注">
        <el-input v-model="form.remark" type="textarea" :rows="2" />
      </el-form-item>
      </div>

      <!-- ============ Step 3: 确认提交 ============ -->
      <div v-show="stepper && currentStep === 2" class="confirm-panel">
        <el-descriptions :column="2" border size="small" title="请确认信息">
          <el-descriptions-item label="结算类型">
            {{ form.settlementType === 'LABOR' ? '劳务结算' : '供应商结算' }}
          </el-descriptions-item>
          <el-descriptions-item label="结算单号">{{ form.orderNo }}</el-descriptions-item>
          <el-descriptions-item label="所属机构">
            {{ isLockedFromMeeting ? lockedOrgName : orgLabel(form.orgId) }}
          </el-descriptions-item>
          <el-descriptions-item label="关联项目">
            {{ isLockedFromMeeting ? lockedProjectName : projectLabel(form.projectId) }}
          </el-descriptions-item>
          <el-descriptions-item label="关联会议" :span="2">
            {{ isLockedFromMeeting ? lockedMeetingName : (selectedMeetingNames || '无') }}
          </el-descriptions-item>
          <el-descriptions-item label="结算模式">{{ modeLabel(form.settlementMode) }}</el-descriptions-item>
          <el-descriptions-item label="收款方式">{{ form.collectWay || '-' }}</el-descriptions-item>
          <el-descriptions-item v-if="form.settlementType === 'SUPPLIER'" label="供应商">
            {{ supplierLabel(form.supplierOrgId) }}
          </el-descriptions-item>
          <el-descriptions-item v-if="form.settlementType === 'SUPPLIER'" label="采购方">
            {{ orgLabel(form.buyerOrgId) }}
          </el-descriptions-item>
          <el-descriptions-item label="备注" :span="2">{{ form.remark || '-' }}</el-descriptions-item>
        </el-descriptions>
        <div class="confirm-tip">
          <i class="el-icon-info"></i> 创建后将进入"草稿"状态,可继续添加费用明细和上传材料
        </div>
      </div>
    </el-form>

    <div slot="footer">
      <template v-if="stepper">
        <el-button size="small" @click="innerVisible = false">取消</el-button>
        <el-button v-if="currentStep > 0" size="small" @click="prevStep">
          上一步
        </el-button>
        <el-button v-if="currentStep < 2" size="small" type="primary" @click="nextStep">
          下一步
        </el-button>
        <el-button v-else size="small" type="primary" :loading="saving" @click="doSave">
          确认创建
        </el-button>
      </template>
      <template v-else>
        <el-button size="small" @click="innerVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="saving" @click="doSave">
          {{ form.id ? '保存' : '创建' }}
        </el-button>
      </template>
    </div>
  </el-dialog>
</template>

<script>
import {
  createSpendingOrder,
  getProjectListNew, getMeetingList
} from '@/request/api.js';
import VirtualSelect from '@/components/VirtualSelect.vue';

export default {
  name: 'SpendingOrderDialog',
  components: { VirtualSelect },
  props: {
    visible: { type: Boolean, default: false },
    defaultType: { type: String, default: 'SUPPLIER' },
    // 由父组件预加载并传入(进入"结算单管理"页面时一次性加载)
    orgOptions: { type: Array, default: () => [] },
    supplierOptions: { type: Array, default: () => [] },
    collectWayOptions: { type: Array, default: () => [] },
    // G11: 从"我的会议"跳过来时预填的会议(含 id/orgId/projectId/meetingName)
    prefillMeeting: { type: Object, default: null },
    // 步骤条模式 (从我的会议直接发起时启用,分 3 步)
    stepper: { type: Boolean, default: false }
  },
  data() {
    return {
      saving: false,
      form: this.emptyForm(),
      selectedMeetingIds: [],
      projectOptions: [], meetingOptions: [],
      projectLoading: false, meetingLoading: false,
      // 大列表渲染封顶 100 条, query 跟踪每个 el-select 的搜索输入
      orgHostQuery: '', orgBuyerQuery: '', supplierQuery: '',
      maxRender: 30,
      // 步骤条当前步骤 (0/1/2)
      currentStep: 0,
      rules: {
        settlementType: [{ required: true, message: '请选择结算类型', trigger: 'change' }],
        orderNo: [{ required: true, message: '结算单号必填', trigger: 'blur' }],
        orgId: [{ required: true, message: '请选择所属机构', trigger: 'change' }],
        projectId: [{ required: true, message: '请选择关联项目', trigger: 'change' }],
        settlementMode: [{ required: true, message: '请选择结算模式', trigger: 'change' }]
      }
    };
  },
  computed: {
    innerVisible: {
      get() { return this.visible; },
      set(v) { this.$emit('update:visible', v); }
    },
    dialogTitle() {
      return this.form.id ? '编辑结算单' : '新建结算单';
    },
    // ===== 大列表本地过滤 + 渲染封顶 =====
    filteredHostOrgs() { return this.filterOrgs(this.orgOptions, this.orgHostQuery); },
    visibleHostOrgs() { return this.filteredHostOrgs.slice(0, this.maxRender); },
    totalHostOrgs() { return this.filteredHostOrgs.length; },

    filteredBuyerOrgs() { return this.filterOrgs(this.orgOptions, this.orgBuyerQuery); },
    visibleBuyerOrgs() { return this.filteredBuyerOrgs.slice(0, this.maxRender); },
    totalBuyerOrgs() { return this.filteredBuyerOrgs.length; },

    filteredSuppliers() { return this.filterOrgs(this.supplierOptions, this.supplierQuery); },
    visibleSuppliers() { return this.filteredSuppliers.slice(0, this.maxRender); },
    totalSuppliers() { return this.filteredSuppliers.length; },

    // 是否锁定模式: 从我的会议跳过来,机构/项目/会议已由会议确定,不允许修改
    isLockedFromMeeting() {
      return !!(this.prefillMeeting && this.prefillMeeting.id);
    },
    lockedOrgName() {
      const pm = this.prefillMeeting;
      if (!pm) return '-';
      // 优先 prefill 自带 → 找 orgOptions → 兜底 ID
      if (pm.orgName) return pm.orgName;
      if (pm.orgId && this.orgOptions && this.orgOptions.length) {
        const found = this.orgOptions.find(o => String(o.id) === String(pm.orgId));
        if (found) return found.orgName;
      }
      return pm.orgId || '-';
    },
    lockedProjectName() {
      const pm = this.prefillMeeting;
      if (!pm) return '-';
      if (pm.projectName) return pm.projectName;
      if (pm.projectId && this.projectOptions && this.projectOptions.length) {
        const found = this.projectOptions.find(p => String(p.id) === String(pm.projectId));
        if (found) return found.projectName;
      }
      return pm.projectId || '-';
    },
    lockedMeetingName() {
      const pm = this.prefillMeeting;
      if (!pm) return '-';
      return pm.meetingName || pm.meetingTitle || pm.id || '-';
    },

    // 步骤 3 确认面板用的展示名
    selectedMeetingNames() {
      if (!this.selectedMeetingIds.length) return '';
      return this.selectedMeetingIds.map(id => {
        const m = this.meetingOptions.find(o => o.id === id);
        return m ? m.meetingName : id;
      }).join('、');
    }
  },
  methods: {
    emptyForm() {
      return {
        id: null, orderNo: '', settlementType: this.defaultType,
        orgId: null, projectId: null, meetingId: null,
        settlementMode: 'PROJECT', settlementThreshold: 0,
        linkedMeetingIds: '',
        supplierOrgId: null, supplierName: '',
        buyerOrgId: null, buyerName: '',
        collectWay: '', remark: ''
      };
    },
    async handleOpen() {
      // 仅重置表单, 静态下拉数据由父组件预加载, 这里零请求
      this.form = this.emptyForm();
      this.form.settlementType = this.defaultType;
      this.selectedMeetingIds = [];
      this.form.orderNo = this.genOrderNo(this.defaultType);
      this.projectOptions = [];
      this.meetingOptions = [];
      this.currentStep = 0;

      // G11: 若有 prefillMeeting, 锁定机构/项目/会议(显示为静态文本)
      // 这种场景属性已由会议唯一确定, 无需拉全量列表, 也不允许用户修改
      if (this.prefillMeeting) {
        const pm = this.prefillMeeting;
        this.form.orgId = pm.orgId;
        this.form.projectId = pm.projectId;
        this.form.meetingId = pm.id;
        this.selectedMeetingIds = pm.id ? [pm.id] : [];
        this.form.settlementMode = 'PER_MEETING';
        // 注意: 不调用 onOrgChange / onProjectChange, 不发联动请求, 节省网络
        //      展示名直接走 lockedOrgName / lockedProjectName / lockedMeetingName computed
      }
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate());
    },

    /** 保证 projectOptions 里有预填的项目(若加载结果不包含,则注入兜底项) */
    ensureProjectOptionExists(pm) {
      if (!pm.projectId) return;
      const exists = this.projectOptions.some(p => p.id === pm.projectId);
      if (!exists && pm.projectName) {
        this.projectOptions = [{ id: pm.projectId, projectName: pm.projectName }, ...this.projectOptions];
      }
    },
    /** 保证 meetingOptions 里有预填的会议 */
    ensureMeetingOptionExists(pm) {
      if (!pm.id) return;
      const exists = this.meetingOptions.some(m => m.id === pm.id);
      const meetingName = pm.meetingName || pm.meetingTitle;
      if (!exists && meetingName) {
        this.meetingOptions = [{ id: pm.id, meetingName }, ...this.meetingOptions];
      }
    },

    // ===== 步骤条导航 =====
    async nextStep() {
      // 校验当前 step 必填字段; 锁定模式下 orgId/projectId 由会议确定,不需要走表单校验
      const baseFields = ['settlementType', 'orderNo'];
      if (!this.isLockedFromMeeting) {
        baseFields.push('orgId', 'projectId');
      }
      const fieldsByStep = {
        0: baseFields,
        1: ['settlementMode']
      };
      const toValidate = fieldsByStep[this.currentStep];
      if (!toValidate || !this.$refs.formRef) {
        this.currentStep = Math.min(2, this.currentStep + 1);
        return;
      }
      // Element UI 的 validateField 传数组时,回调会对每个字段单独触发一次
      // 用 Promise.all 等所有字段都验证完, 一次性判断结果, 避免步数被多次累加
      // 额外加 800ms 超时兜底, 防止极端情况下回调未触发导致死锁(切步骤时 el-form 内部状态可能短暂不一致)
      const fieldChecks = toValidate.map(field => Promise.race([
        new Promise(resolve => this.$refs.formRef.validateField(field, err => resolve(err))),
        new Promise(resolve => setTimeout(() => resolve(''), 800))
      ]));
      const errors = await Promise.all(fieldChecks);
      if (errors.every(e => !e)) {
        this.currentStep = Math.min(2, this.currentStep + 1);
      }
    },
    prevStep() {
      this.currentStep = Math.max(0, this.currentStep - 1);
    },

    // ===== 确认面板 label 解析 =====
    orgLabel(id) {
      if (!id) return '-';
      const o = this.orgOptions.find(x => x.id === id);
      return o ? o.orgName : id;
    },
    projectLabel(id) {
      if (!id) return '-';
      const p = this.projectOptions.find(x => x.id === id);
      return p ? p.projectName : id;
    },
    supplierLabel(id) {
      if (!id) return '-';
      const s = this.supplierOptions.find(x => x.id === id);
      return s ? s.orgName : id;
    },
    modeLabel(m) {
      return { PROJECT: '按项目结算', BATCH: '按 N 场会结算', PER_MEETING: '每场会单结' }[m] || m || '-';
    },
    genOrderNo(type) {
      const prefix = type === 'LABOR' ? 'LABOR' : 'SUPP';
      const d = new Date();
      const ts = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
      return `${prefix}-${ts}`;
    },

    filterOrgs(list, q) {
      if (!q) return list || [];
      const kw = q.toLowerCase();
      return (list || []).filter(o => (o.orgName || '').toLowerCase().includes(kw));
    },

    async onOrgChange(orgId) {
      this.form.projectId = null;
      this.form.meetingId = null;
      this.projectOptions = [];
      this.meetingOptions = [];
      this.selectedMeetingIds = [];
      if (!orgId) return;
      this.projectLoading = true;
      try {
        const resp = await getProjectListNew({ orgId, page: 1, size: 200 });
        if (resp.code === 200) {
          this.projectOptions = (resp.data && resp.data.records) || resp.data || [];
        }
      } finally { this.projectLoading = false; }
    },
    async onProjectChange(projectId) {
      this.form.meetingId = null;
      this.meetingOptions = [];
      this.selectedMeetingIds = [];
      if (!projectId) return;
      this.meetingLoading = true;
      try {
        const resp = await getMeetingList({ projectId, page: 1, size: 200 });
        if (resp.code === 200) {
          this.meetingOptions = (resp.data && resp.data.records) || resp.data || [];
        }
      } finally { this.meetingLoading = false; }
    },
    onSupplierChange(id) {
      const found = this.supplierOptions.find(o => o.id === id);
      this.form.supplierName = found ? found.orgName : '';
    },
    onBuyerChange(id) {
      const found = this.orgOptions.find(o => o.id === id);
      this.form.buyerName = found ? found.orgName : '';
    },

    doSave() {
      this.$refs.formRef.validate(async valid => {
        if (!valid) return;
        if (this.selectedMeetingIds.length > 0) {
          this.form.meetingId = this.selectedMeetingIds[0];
          this.form.linkedMeetingIds = this.selectedMeetingIds.join(',');
        } else {
          this.form.linkedMeetingIds = '';
        }

        this.saving = true;
        try {
          const resp = await createSpendingOrder(this.form);
          if (resp.code === 200) {
            this.$message.success('结算单已创建');
            this.innerVisible = false;
            this.$emit('created', resp.data);
          } else {
            this.$message.error(resp.msg || '创建失败');
          }
        } catch (e) {
          this.$message.error('创建失败: ' + (e.message || e));
        } finally {
          this.saving = false;
        }
      });
    }
  }
};

function pad(n) { return String(n).padStart(2, '0'); }
</script>

<style scoped>
.form-hint { color: #909399; font-size: 12px; line-height: 1.4; }
.more-tip { padding: 6px 12px; color: #909399; font-size: 12px; background: #FAFAFA; border-top: 1px solid #EBEEF5; }
.confirm-panel { padding: 4px; }
.confirm-tip { margin-top: 12px; padding: 10px 12px; background: #F4F8FF; border-radius: 4px; color: #409EFF; font-size: 13px; }
.confirm-tip i { margin-right: 6px; }
.readonly-field {
  display: flex; align-items: center; gap: 6px;
  padding: 4px 12px; min-height: 32px;
  background: #F5F7FA; border: 1px dashed #DCDFE6; border-radius: 4px;
  color: #303133;
}
.readonly-field i { color: #909399; }
.readonly-field span { flex: 1; }
</style>
